import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { platformAPI, fileUrl } from '../../services/api';
import { Menu, X, ArrowRight } from 'lucide-react';
import '../../styles/landing.css';

/* Brand icons (lucide v1.x has no social icons) */
const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5h1.65V3.6c-.3-.04-1.3-.1-2.45-.1-2.4 0-4.05 1.46-4.05 4.15v2.25H7.5V13h2.7v8h3.3z"/></svg>
);
const TwitterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22 5.9c-.74.33-1.53.55-2.36.65a4.1 4.1 0 0 0 1.8-2.27c-.79.47-1.66.8-2.6.99A4.1 4.1 0 0 0 11.75 9c0 .32.03.63.1.93A11.65 11.65 0 0 1 3.4 5.64a4.1 4.1 0 0 0 1.27 5.48c-.67-.02-1.3-.2-1.86-.5v.05a4.1 4.1 0 0 0 3.3 4.03 4.1 4.1 0 0 1-1.86.07 4.11 4.11 0 0 0 3.83 2.85A8.23 8.23 0 0 1 2 19.29a11.62 11.62 0 0 0 6.29 1.84c7.55 0 11.68-6.25 11.68-11.67v-.53c.8-.58 1.5-1.3 2.03-2.13z"/></svg>
);
const LinkedinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6.94 8.5v12H3.56v-12h3.38zM5.25 3a1.97 1.97 0 1 1 0 3.94 1.97 1.97 0 0 1 0-3.94zM20.5 13.9v6.6h-3.37v-6.2c0-1.55-.56-2.6-1.96-2.6-1.07 0-1.7.72-1.98 1.41-.1.25-.13.59-.13.93v6.46H9.69s.04-10.48 0-11.57h3.37v1.64c.45-.69 1.25-1.68 3.04-1.68 2.22 0 4.4 1.45 4.4 5z"/></svg>
);
const YoutubeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M21.6 7.2a2.5 2.5 0 0 0-1.76-1.77C18.28 5 12 5 12 5s-6.28 0-7.84.43A2.5 2.5 0 0 0 2.4 7.2 26.2 26.2 0 0 0 2 12c0 1.61.13 3.22.4 4.8a2.5 2.5 0 0 0 1.76 1.77C5.72 19 12 19 12 19s6.28 0 7.84-.43a2.5 2.5 0 0 0 1.76-1.77c.27-1.58.4-3.19.4-4.8s-.13-3.22-.4-4.8z"/></svg>
);

const DEFAULT_FOOTER = {
  about: 'LedgerPro is professional double-entry accounting software for modern businesses — invoicing, expenses, banking, inventory and reporting in one place.',
  columns: [
    { title: 'Product', links: [{ label: 'Features', url: '#features' }, { label: 'Pricing', url: '#pricing' }, { label: 'Security', url: '/security' }] },
    { title: 'Company', links: [{ label: 'Contact Sales', url: '/contact' }, { label: 'Privacy Policy', url: '/privacy-policy' }, { label: 'Terms of Service', url: '/terms-of-service' }] },
    { title: 'Get started', links: [{ label: 'Create account', url: '/register' }, { label: 'Sign in', url: '/login' }, { label: 'View plans', url: '#pricing' }] },
  ],
};

/** Shared landing navbar — used by the landing page AND the legal/contact pages. */
export function LandingHeader() {
  const { user } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [platform, setPlatform] = useState({});
  const [brandName, setBrandName] = useState('LedgerPro');

  useEffect(() => {
    platformAPI.getPublicSettings().then(p => setPlatform(p)).catch(() => {});
    platformAPI.getLanding().then(d => {
      if (d.content?.brand_name) setBrandName(d.content.brand_name);
    }).catch(() => {});
  }, []);

  // On sub-pages, section anchors must jump back to the home page
  const onHome = location.pathname === '/';
  const hash = (id) => (onHome ? '' : '/') + '#' + id;

  const LogoMark = ({ size = 32 }) => platform.logo_path ? (
    <img src={fileUrl(platform.logo_path)} alt={brandName} className="lp-logo-img" style={{ height: size }} />
  ) : (
    <span className="lp-logo-mark" style={{ width: size, height: size }}>{brandName.charAt(0).toUpperCase()}</span>
  );

  const isCompanyUser = !!user?.company_id;
  const isPlatformUser = !!user && !user.company_id;

  const navLinks = [
    { label: 'Features', href: hash('features') },
    { label: 'Why us', href: hash('showcase') },
    { label: 'Pricing', href: hash('pricing') },
    { label: 'FAQ', href: hash('faq') },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <header className="lp-header">
      <div className="lp-container lp-header-inner">
        <Link to="/" className="lp-logo">
          <LogoMark />
          <span>{brandName}</span>
        </Link>

        <nav className={`lp-nav ${menuOpen ? 'open' : ''}`}>
          {navLinks.map(l =>
            l.href.startsWith('/') ? (
              <Link key={l.label} to={l.href} onClick={() => setMenuOpen(false)}>{l.label}</Link>
            ) : (
              <a key={l.label} href={l.href} onClick={() => setMenuOpen(false)}>{l.label}</a>
            )
          )}
        </nav>

        <div className="lp-header-actions">
          {(isCompanyUser || isPlatformUser) ? (
            <>
              {isPlatformUser && <Link to="/super-admin" className="lp-btn lp-btn-ghost">Admin Portal</Link>}
              <Link to="/dashboard" className="lp-btn lp-btn-primary">Go to Dashboard <ArrowRight size={15} /></Link>
            </>
          ) : (
            <>
              <Link to="/login" className="lp-btn lp-btn-ghost">Sign in</Link>
              <Link to="/register" className="lp-btn lp-btn-primary">Start free trial</Link>
            </>
          )}
          <button className="lp-burger" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
    </header>
  );
}

/** Shared landing footer — content comes from the Super Admin CMS. */
export function LandingFooter({ content }) {
  const [platform, setPlatform] = useState({});
  const [fetched, setFetched] = useState(content || null);

  useEffect(() => {
    platformAPI.getPublicSettings().then(p => setPlatform(p)).catch(() => {});
    if (!content) {
      platformAPI.getLanding().then(d => setFetched(d.content || DEFAULT_FOOTER)).catch(() => {});
    }
  }, [content]);

  const c = fetched || content || DEFAULT_FOOTER;
  const brandName = c.brand_name || 'LedgerPro';

  return (
    <footer className="lp-footer">
      <div className="lp-container lp-footer-grid">
        <div className="lp-footer-brand">
          <Link to="/" className="lp-logo">
            {platform.logo_path ? (
              <img src={fileUrl(platform.logo_path)} alt={brandName} className="lp-logo-img" style={{ height: 30 }} />
            ) : (
              <span className="lp-logo-mark" style={{ width: 30, height: 30 }}>{brandName.charAt(0).toUpperCase()}</span>
            )}
            <span>{brandName}</span>
          </Link>
          <p>{c.footer?.about || DEFAULT_FOOTER.about}</p>
          <div className="lp-socials">
            <a href="#" aria-label="Facebook"><FacebookIcon /></a>
            <a href="#" aria-label="Twitter"><TwitterIcon /></a>
            <a href="#" aria-label="LinkedIn"><LinkedinIcon /></a>
            <a href="#" aria-label="YouTube"><YoutubeIcon /></a>
          </div>
        </div>
        {(c.footer?.columns?.length ? c.footer.columns : DEFAULT_FOOTER.columns).map((col, i) => (
          <div key={i} className="lp-footer-col">
            <h4>{col.title}</h4>
            {(col.links || []).map((l, j) =>
              l.url?.startsWith('/') ? (
                <Link key={j} to={l.url}>{l.label}</Link>
              ) : (
                <a key={j} href={l.url || '#'}>{l.label}</a>
              )
            )}
          </div>
        ))}
      </div>
      <div className="lp-container lp-footer-bottom">
        <span>© {new Date().getFullYear()} {brandName}. All rights reserved.</span>
        <div className="lp-footer-links">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms-of-service">Terms of Service</Link>
          <Link to="/security">Security</Link>
          <Link to="/contact">Contact</Link>
        </div>
      </div>
    </footer>
  );
}

export default LandingHeader;
